import * as THREE from "three";
import { getHFieldWithAppliedHeightScale } from "../../utils/terrainFloor";
import { getHFieldHeightRange, sampleHFieldHeightAtModelXY } from "../../utils/hfieldModelSampling";
import { findJointVizGroupInScene } from "./sceneObjectLookups";

export function intersectLocalRayWithHField(rayOrigin, rayDirection, hfield) {
  const effective = getHFieldWithAppliedHeightScale(hfield);
  if (!effective || !Array.isArray(effective.size)) return null;
  const range = getHFieldHeightRange(hfield);
  if (!range || Math.abs(rayDirection.z) < 1e-9) return null;

  const xHalf = Number(effective.size[0]) || 1;
  const yHalf = Number(effective.size[1]) || xHalf;
  const bounds = {
    min: new THREE.Vector3(-xHalf, -yHalf, range.min - 0.5),
    max: new THREE.Vector3(xHalf, yHalf, range.max + 0.5),
  };
  let tStart = 0;
  let tEnd = Infinity;
  const axes = ["x", "y", "z"];
  for (const axis of axes) {
    const origin = rayOrigin[axis];
    const direction = rayDirection[axis];
    if (Math.abs(direction) < 1e-9) {
      if (origin < bounds.min[axis] || origin > bounds.max[axis]) return null;
      continue;
    }
    let t0 = (bounds.min[axis] - origin) / direction;
    let t1 = (bounds.max[axis] - origin) / direction;
    if (t0 > t1) [t0, t1] = [t1, t0];
    tStart = Math.max(tStart, t0);
    tEnd = Math.min(tEnd, t1);
    if (tEnd < tStart) return null;
  }
  if (!Number.isFinite(tStart) || !Number.isFinite(tEnd) || tEnd <= 0 || tEnd <= tStart)
    return null;

  const evalAt = (t) => {
    const point = rayOrigin.clone().addScaledVector(rayDirection, t);
    const height = sampleHFieldHeightAtModelXY(hfield, point.x, point.y);
    if (height == null || !Number.isFinite(height)) return null;
    return { point, value: point.z - height };
  };

  let prev = null;
  let prevT = null;
  let best = null;
  const steps = 72;
  for (let i = 0; i <= steps; i += 1) {
    const t = tStart + ((tEnd - tStart) * i) / steps;
    const cur = evalAt(t);
    if (!cur) continue;
    if (!best || Math.abs(cur.value) < Math.abs(best.value)) best = cur;
    if (prev && prev.value * cur.value <= 0) {
      let lo = prevT;
      let hi = t;
      let loEval = prev;
      for (let iter = 0; iter < 18; iter += 1) {
        const mid = (lo + hi) * 0.5;
        const midEval = evalAt(mid);
        if (!midEval) break;
        if (loEval.value * midEval.value <= 0) {
          hi = mid;
          cur.point.copy(midEval.point);
        } else {
          lo = mid;
          loEval = midEval;
        }
      }
      return evalAt((lo + hi) * 0.5)?.point || cur.point;
    }
    prev = cur;
    prevT = t;
  }
  return best && Math.abs(best.value) < 0.05 ? best.point : null;
}

export function getJointPositionSliderScreenPosition(scene, camera, renderer, jointIdentifier) {
  if (!scene || !camera || !renderer || !jointIdentifier) return;
  const viz = findJointVizGroupInScene(scene, jointIdentifier);
  if (!viz) return null;
  const rect = renderer.domElement?.getBoundingClientRect?.();
  if (!rect || rect.width <= 0 || rect.height <= 0) return null;

  const worldPosition = new THREE.Vector3();
  viz.getWorldPosition(worldPosition);
  const projected = worldPosition.project(camera);
  const projectedClientX = rect.left + (projected.x * 0.5 + 0.5) * rect.width;
  const projectedClientY = rect.top + (-projected.y * 0.5 + 0.5) * rect.height;
  const overlayWidth = 112;
  const overlayHeight = 16;
  return {
    x: THREE.MathUtils.clamp(projectedClientX + 36, rect.left + 8, rect.right - overlayWidth - 8),
    y: THREE.MathUtils.clamp(
      projectedClientY,
      rect.top + overlayHeight * 0.5 + 8,
      rect.bottom - overlayHeight * 0.5 - 8
    ),
  };
}

export function dispatchCritterToast(message) {
  try {
    window.dispatchEvent(new CustomEvent("critter-toast", { detail: { message } }));
  } catch {
    // Toasts are best-effort UI feedback.
  }
}

/** For {@link useStoreWithEqualityFn}: ignore physics-store churn unrelated to this joint's slider snapshot. */
export function equalJointActuatorSliderSnapshot(prev, next) {
  if (prev === next) return true;
  if (prev == null && next == null) return true;
  if (!prev || !next) return false;
  return (
    prev.min === next.min &&
    prev.max === next.max &&
    prev.step === next.step &&
    prev.value === next.value &&
    prev.controlKey === next.controlKey &&
    prev.actuatorDisplayLabel === next.actuatorDisplayLabel
  );
}
